import type { Provider as ProviderScheme } from '@lib/graphql/generated';
import { providers } from '@utils/providers';
import { Image, type PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import { twMerge } from 'tailwind-merge';
import { Link } from '../Link/Link';

export interface ProviderProps extends PropsWithClassProps<'onlyText' | 'boxStyle' | 'onlyImage' | 'image'> {
  providerSlug?: string;
  // Callers pass either a hardcoded entry from @utils/providers or a query result that
  // selects a handful of Provider fields, so only a subset of the schema type is present.
  provider?: Partial<ProviderScheme>;
  text?: string;
  link?: string;
  style?: 'text' | 'box' | 'button' | 'onlyImage';
  color?: 'default' | 'white';
  featured?: boolean;
}

export const Provider = ({
  provider,
  providerSlug,
  style,
  featured = false,
  link,
  color = 'default',
  text = 'ansehen',
  classProps,
}: ProviderProps) => {
  const providerData = provider || providers.find((e) => e.slug === providerSlug);

  const providerStyle = twMerge(
    'w-full rounded-full px-4 py-3 grid grid-cols-[auto_1fr] justify-between gap-4 border-0.5 border-gray-650/25 bg-white shadow-card-sm dark:bg-gray-820 hover:border-gray-650/75 dark:hover:border-white group transition-colors duration-150 min-w-[17rem] items-center cursor-pointer',
    featured && 'p-4 rounded-xl border-gray-650/25 dark:border-gray-650/65 h-full',
    classProps?.root,
  );

  const logo = providers.find((e) => e.slug === providerData?.slug)?.[color === 'white' ? 'logoBw' : 'logo'];

  return (
    <>
      {style === 'button' ? (
        link ? (
          <div className={featured ? 'row-span-2' : ''}>
            <a href={link} target="_blank" rel="noreferrer">
              <div className={providerStyle}>
                <div className={twMerge('grid h-4 w-10 items-center', featured && 'h-10 w-16')}>
                  {logo && (
                    <Image
                      src={logo}
                      alt={providerData?.name || providerSlug}
                      width={100}
                      height={60}
                      classProps={{ root: twMerge('absolute w-full h-full object-left', classProps?.image) }}
                      copyright=""
                    />
                  )}
                </div>
                <div className="justify-self-end">
                  {featured && (
                    <div className="text-right font-bold text-sm uppercase leading-header tracking-2px">
                      Auf {providerData?.name} ansehen
                    </div>
                  )}
                  <div
                    className={twMerge(
                      'font-bold text-3xs text-gray-500 uppercase leading-body tracking-2px transition-colors duration-150 group-hover:text-gray-820 dark:group-hover:text-white',
                      featured && 'text-right font-normal text-sm normal-case tracking-normal',
                    )}
                  >
                    {text}
                  </div>
                </div>
              </div>
            </a>
          </div>
        ) : (
          <div className={providerStyle}>
            {logo && (
              <div className="grid items-center">
                <Image
                  src={logo}
                  alt={providerData?.name || providerSlug}
                  width={100}
                  height={40}
                  copyright=""
                  classProps={{ root: classProps?.image }}
                />
              </div>
            )}
            <div className={twMerge('justify-self-end font-bold text-3xs text-gray-500 uppercase tracking-2px')}>
              {text}
            </div>
          </div>
        )
      ) : style === 'box' ? (
        <Link
          href={`/provider/${provider.slug}`}
          className={twMerge(
            'grid aspect-square min-h-36 min-w-36 cursor-pointer items-center justify-center rounded-lg border-0.5 border-primary/0 bg-gradient-to-b from-gray-650/25 via-gray-500/25 to-gray-650/25 p-2 text-center font-bold text-sm text-white uppercase tracking-2px transition-colors duration-150 hover:border-primary',
            classProps?.boxStyle,
          )}
        >
          {logo ? (
            <Image
              src={logo}
              alt={providerData?.name}
              width="96"
              height="72"
              copyright=""
              classProps={{ root: classProps?.image }}
            />
          ) : (
            <span className="hyphenate">{providerData?.name}</span>
          )}
        </Link>
      ) : style === 'onlyImage' ? (
        <div
          className={twMerge(
            'aspect-square min-h-36 min-w-36 items-center justify-center p-2 text-center font-bold text-sm text-white uppercase tracking-2px',
            classProps?.onlyImage,
          )}
        >
          {logo ? (
            <Image
              src={logo}
              alt={providerData?.name}
              width="96"
              height="72"
              copyright=""
              classProps={{ root: classProps?.image }}
            />
          ) : (
            <span className={twMerge('hyphenate', classProps.image)}>{providerData?.name}</span>
          )}
        </div>
      ) : (
        <div className={twMerge('font-bold text-sm uppercase tracking-2px', classProps?.onlyText)}>
          {providerData?.name}
        </div>
      )}
    </>
  );
};
